import { useState, useCallback, useRef } from 'react';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { ReviewCaveat } from '../components/ReviewAid';
import { processBuckets } from '../utils/cdrRecords';
import CDRReport, { CARRIERS } from '../components/cdr/Report';

// ── Main Component ────────────────────────────────────────────────────

export default function CDRAnalyzer() {
  const [phase, setPhase]           = useState('upload');
  const [cdrData, setCdrData]       = useState(null);
  const [buckets, setBuckets]       = useState({ att: [], verizon: [], tmobile: [] });
  const [error, setError]           = useState('');
  const [draggingBucket, setDraggingBucket] = useState(null);
  const attRef = useRef(), verizonRef = useRef(), tmobileRef = useRef();
  const bucketRefs = { att: attRef, verizon: verizonRef, tmobile: tmobileRef };

  // ── Upload: files are dropped straight into their carrier's bucket ───
  const addFiles = useCallback((carrier, fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setError('');
    setBuckets(b => ({ ...b, [carrier]: [...b[carrier], ...arr] }));
  }, []);

  const removeFile = (carrier, idx) => setBuckets(b => ({ ...b, [carrier]: b[carrier].filter((_, i) => i !== idx) }));

  const runProcess = useCallback(async () => {
    trackToolAction('/cdranalyzer', 'CDR Analyzer');
    setPhase('processing');
    try {
      const data = await processBuckets(buckets);
      if (!data.records.length) throw new Error('No parseable call/text/data records were found. For Verizon, make sure the voice CDR files (CDR-4g_VoLTE or VCE-5g-Core-Voice) are included. PDFs are ignored — only text/CSV/Excel records are parsed.');
      setCdrData(data);
      setPhase('dashboard');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to process the files.');
      setPhase('error');
    }
  }, [buckets]);

  const resetAll = () => { setPhase('upload'); setCdrData(null); setBuckets({ att: [], verizon: [], tmobile: [] }); setError(''); };

  const totalFiles = buckets.att.length + buckets.verizon.length + buckets.tmobile.length;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", background: '#f8fafc' }}>
      <Navbar pageTitle="Phone Record Processing" />

      {/* ── UPLOAD (three carrier buckets) ── */}
      {phase === 'upload' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 40 }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <PhoneInTalkIcon style={{ fontSize: 30, color: '#0369a1' }} />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Phone Record Processing</h2>
              <p style={{ margin: '0 auto', maxWidth: 560, color: '#64748b', fontSize: 13, lineHeight: 1.65 }}>
                Drop each carrier's return files into its own box below — multiple responses at
                once. PDFs are accepted but ignored; only text/CSV/Excel records are parsed.
                Duplicate records across overlapping returns are removed automatically.
              </p>
            </div>
            <ReviewCaveat what="the actual returns from the carrier" style={{ maxWidth: 760, margin: '0 auto 24px' }} />

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
              {['att', 'verizon', 'tmobile'].map(key => {
                const info = CARRIERS[key];
                const files = buckets[key];
                const ref = bucketRefs[key];
                return (
                  <div key={key} style={{ flex: '1 1 260px', minWidth: 240, maxWidth: 300 }}>
                    <div
                      onDrop={e => { e.preventDefault(); setDraggingBucket(null); if (e.dataTransfer.files.length) addFiles(key, e.dataTransfer.files); }}
                      onDragOver={e => { e.preventDefault(); setDraggingBucket(key); }}
                      onDragLeave={() => setDraggingBucket(null)}
                      onClick={() => ref.current?.click()}
                      style={{
                        border: `2px dashed ${draggingBucket === key ? info.color : info.border}`,
                        borderRadius: 12, padding: 22, textAlign: 'center', cursor: 'pointer',
                        background: draggingBucket === key ? info.bg : '#fff', transition: 'all .2s', minHeight: 110,
                      }}
                    >
                      <input ref={ref} type="file" multiple accept=".csv,.txt,.xlsx,.xls,.pdf" style={{ display: 'none' }}
                        onChange={e => { if (e.target.files.length) addFiles(key, e.target.files); e.target.value = ''; }} />
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: info.bg, color: info.color, border: `1px solid ${info.border}` }}>{info.label}</span>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
                        {files.length ? `${files.length} file${files.length !== 1 ? 's' : ''} added` : 'Drop files or click to browse'}
                      </div>
                    </div>
                    {key === 'att' && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 6, textAlign: 'center' }}>
                        Do not upload the ReportStirShaken file
                      </div>
                    )}
                    {files.length > 0 && (
                      <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                        {files.map((f, i) => {
                          const isPdf = /\.pdf$/i.test(f.name);
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderBottom: i < files.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: 11.5, opacity: isPdf ? 0.55 : 1 }}>
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#0f172a', textDecoration: isPdf ? 'line-through' : 'none' }}>{f.name}</span>
                              {isPdf && <span style={{ color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>ignored</span>}
                              <button onClick={e => { e.stopPropagation(); removeFile(key, i); }} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, padding: 0, flexShrink: 0 }}>✕</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {error && <div style={{ marginTop: 16, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 26 }}>
              <button onClick={runProcess} disabled={!totalFiles} style={{ padding: '12px 32px', background: totalFiles ? '#0369a1' : '#e2e8f0', color: totalFiles ? '#fff' : '#94a3b8', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: totalFiles ? 'pointer' : 'not-allowed', boxShadow: totalFiles ? '0 4px 14px rgba(3,105,161,.25)' : 'none' }}>
                Process {totalFiles} File{totalFiles !== 1 ? 's' : ''} →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PROCESSING ── */}
      {phase === 'processing' && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ width: 48, height: 48, border: '3px solid #e0f2fe', borderTopColor: '#0369a1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 15 }}>Processing records…</div>
        </div>
      )}

      {/* ── ERROR ── */}
      {phase === 'error' && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 44 }}>⚠️</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Couldn’t process the files</div>
          <div style={{ fontSize: 13, color: '#64748b', maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>{error}</div>
          <button onClick={() => setPhase('upload')} style={{ padding: '10px 22px', background: '#0369a1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      )}

      {/* ── DASHBOARD ── */}
      {phase === 'dashboard' && cdrData && <CDRReport data={cdrData} onReset={resetAll} />}
    </div>
  );
}
